import React, {
  FC,
  useState,
  KeyboardEvent,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { get } from 'lodash';
import MenuItem from '../MenuItem';
import { InfoTooltip, TooltipPosition } from '../InfoTooltip';
import styles from './GroupByListView.module.css';
import {
  CategorizedList,
  GroupTooltipConfig,
} from '../../../types/SelectInput';

type GroupByListViewProps = {
  valueProperty: string;
  list: CategorizedList[];
  onChange: (value: string) => void;
  multiSelect?: boolean;
  hasOptionSelected: (option: string) => boolean;
  groupTooltips?: GroupTooltipConfig;
};

const GroupByListView: FC<GroupByListViewProps> = React.memo(
  ({
    valueProperty,
    list = [],
    onChange,
    multiSelect = false,
    hasOptionSelected,
    groupTooltips = {},
  }) => {
    const [groupIndices, setGroupIndices] = useState([0, -1]);
    const [tooltipPositions, setTooltipPositions] = useState<
      Record<string, TooltipPosition>
    >({});
    const itemRefs = useRef<Array<Array<HTMLDivElement | null>>>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const groupTitleRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Function to calculate tooltip position based on scroll and element position
    const calculateTooltipPosition = useCallback(
      (groupTitle: string) => {
        const container = containerRef.current;
        const titleElement = groupTitleRefs.current[groupTitle];

        if (!container || !titleElement) {
          return (
            groupTooltips[groupTitle]?.position || TooltipPosition.TOP_RIGHT
          );
        }

        const containerRect = container.getBoundingClientRect();
        const titleRect = titleElement.getBoundingClientRect();

        // Calculate relative position within the container
        const titleRelativeTop = titleRect.top - containerRect.top;

        // Consider tooltip height and padding
        // If the title is too close to the top of the visible container, use bottom position
        const minSpaceRequired = 80; // Space needed for top tooltip

        if (titleRelativeTop < minSpaceRequired) {
          return TooltipPosition.BOTTOM_RIGHT;
        }

        return groupTooltips[groupTitle]?.position || TooltipPosition.TOP_RIGHT;
      },
      [groupTooltips]
    );

    // Function to update all tooltip positions
    const updateTooltipPositions = useCallback(() => {
      const newPositions: Record<string, TooltipPosition> = {};

      list.forEach(group => {
        if (groupTooltips[group.title]) {
          newPositions[group.title] = calculateTooltipPosition(group.title);
        }
      });

      // Only update state if positions actually changed
      setTooltipPositions(prev => {
        const hasChanged = Object.keys(newPositions).some(
          key => prev[key] !== newPositions[key]
        );
        return hasChanged ? newPositions : prev;
      });
    }, [list, groupTooltips, calculateTooltipPosition]);

    // Scroll handler with immediate update
    const handleScroll = useCallback(() => {
      // Update immediately for better responsiveness
      updateTooltipPositions();
    }, [updateTooltipPositions]);

    const handleSelect = (groupIndex: number, itemIndex: number) => {
      const selectedItem = list[groupIndex].list[itemIndex][valueProperty];
      onChange(selectedItem as string);
    };

    const scrollItemIntoView = (groupIndex: number, itemIndex: number) => {
      itemRefs.current[groupIndex][itemIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      const maxGroupIndex = list.length - 1;
      let [groupIndex, itemIndex] = groupIndices;

      switch (event.key) {
        case 'ArrowDown':
          if (itemIndex < list[groupIndex].list.length - 1) {
            itemIndex += 1;
          } else if (groupIndex < maxGroupIndex) {
            groupIndex += 1;
            itemIndex = 0;
          }
          break;
        case 'ArrowUp':
          if (itemIndex > 0) {
            itemIndex -= 1;
          } else if (groupIndex > 0) {
            groupIndex -= 1;
            itemIndex = list[groupIndex].list.length - 1;
          }
          break;
        case 'Space':
        case 'Enter':
          handleSelect(groupIndex, itemIndex);
          break;
        default:
          return;
      }

      setGroupIndices([groupIndex, itemIndex]);
      scrollItemIntoView(groupIndex, itemIndex);

      // Update tooltip positions after scrolling
      setTimeout(() => {
        updateTooltipPositions();
      }, 100);

      event.preventDefault(); // Prevent default scrolling behavior
    };

    const setItemRef = (
      groupIndex: number,
      itemIndex: number,
      el: HTMLDivElement | null
    ) => {
      if (!itemRefs.current[groupIndex]) {
        itemRefs.current[groupIndex] = [];
      }
      itemRefs.current[groupIndex][itemIndex] = el;
    };

    useEffect(() => {
      itemRefs.current = list.map(group => Array(group.list.length).fill(null));
      // Initial tooltip position calculation
      updateTooltipPositions();
    }, [list, updateTooltipPositions]);

    // Add scroll event listener and resize observer
    useEffect(() => {
      const container = containerRef.current;
      if (container) {
        // Add scroll listener
        container.addEventListener('scroll', handleScroll);

        // Add resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
          updateTooltipPositions();
        });
        resizeObserver.observe(container);

        return () => {
          container.removeEventListener('scroll', handleScroll);
          resizeObserver.disconnect();
        };
      }
      return undefined;
    }, [handleScroll, updateTooltipPositions]);

    return (
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={styles['groupby-list-view-container']}
        role='none'
      >
        {list.map((group, groupIndex) => (
          <div
            key={group.title}
            role='group'
            className={styles['groupby-list-view-container__list-wrapper']}
            aria-labelledby={group.title}
          >
            {group.title && (
              <div
                ref={el => {
                  groupTitleRefs.current[group.title] = el;
                  // Trigger position update when ref is set
                  if (el) {
                    // Use setTimeout to ensure the element is fully rendered
                    setTimeout(() => {
                      updateTooltipPositions();
                    }, 0);
                  }
                }}
                id={group.title}
                className={
                  styles[
                    'groupby-list-view-container__list-wrapper__title-text'
                  ]
                }
              >
                <div className={styles['group-title-wrapper']}>
                  <span className={styles['group-title-text']}>
                    {group.title} ({group.list.length})
                  </span>
                  {groupTooltips[group.title] && (
                    <InfoTooltip
                      id={`${group.title
                        .toLowerCase()
                        .replace(/\s+/g, '-')}-tooltip`}
                      content={groupTooltips[group.title].text}
                      tooltipPosition={
                        tooltipPositions[group.title] ||
                        groupTooltips[group.title].position ||
                        TooltipPosition.TOP_RIGHT
                      }
                      classes={styles['group-title-tooltip']}
                    />
                  )}
                </div>
              </div>
            )}
            {group.list.map((option, index) => (
              <div
                ref={el => setItemRef(groupIndex, index, el)}
                key={option[valueProperty] as string}
              >
                <MenuItem
                  key={option[valueProperty] as string}
                  value={option[valueProperty] as string}
                  isSelected={hasOptionSelected(
                    option[valueProperty] as string
                  )}
                  isActive={
                    groupIndices[0] === groupIndex && groupIndices[1] === index
                  }
                  onClick={onChange}
                  selectable={multiSelect}
                  label={get(option, 'label', '')}
                  isDisabled={option.isDisabled as boolean}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
);

GroupByListView.displayName = 'GroupByListView';

export default GroupByListView;
